import React, { useState } from 'react';
import { X, User } from 'lucide-react';
import { useModal } from '../../../shared/components/ModalProvider';

interface AddGuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (guestName: string) => Promise<void>;
}

const AddGuestModal: React.FC<AddGuestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [guestName, setGuestName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modal = useModal();

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!guestName.trim()) {
      await modal.alert('비회원 이름을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(guestName.trim());
      // 성공 시 초기화 (부모 컴포넌트에서 모달 닫음)
      setGuestName('');
    } catch (error) {
      // 에러는 부모 컴포넌트에서 처리
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setGuestName('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* 모달 */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-20-semibold text-gray-900">비회원 추가</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* 이름 입력 */}
        <div className="mb-4">
          <label className="block text-16-medium text-gray-700 mb-2">
            비회원 이름
          </label>
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="이름을 입력하세요"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main focus:border-transparent text-16-regular"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 text-16-medium rounded-lg transition-colors disabled:opacity-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !guestName.trim()}
            className="flex-1 px-4 py-3 bg-main hover:bg-main-medium text-white text-16-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? '추가 중...' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddGuestModal;
